import React from 'react';
import { HeatmapCellUser } from '../utils/analyticsData';
import { useChartTooltip } from './ChartTooltip';

interface HeatmapProps {
  matrix: number[][];   // [7 weekdays Mon-Sun][12 two-hour buckets]
  users: HeatmapCellUser[][][];
  max: number;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOUR_LABELS = ['00', '', '04', '', '08', '', '12', '', '16', '', '20', ''];

const INTENSITY_COLORS = ['#F4F1F8', '#E8DAFB', '#CBA8F3', '#A96DEC', '#8E42EE'];

function getColor(val: number, max: number): string {
  if (max === 0 || val === 0) return INTENSITY_COLORS[0];
  const ratio = val / max;
  if (ratio <= 0.2) return INTENSITY_COLORS[1];
  if (ratio <= 0.4) return INTENSITY_COLORS[2];
  if (ratio <= 0.7) return INTENSITY_COLORS[3];
  return INTENSITY_COLORS[4];
}

const Heatmap: React.FC<HeatmapProps> = ({ matrix, users, max }) => {
  const { show, move, hide, tooltipEl } = useChartTooltip();

  return (
    <>
      {tooltipEl}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--an-sp-2)' }}>
        {WEEKDAYS.map((day, di) => (
          <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: '0 0 26px', fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--an-text-muted)' }}>
              {day}
            </span>
            <div style={{ flex: 1, display: 'flex', gap: 'var(--an-sp-1)' }}>
              {(matrix[di] ?? Array(12).fill(0)).map((val, bi) => {
                const cellUsers = users[di]?.[bi] ?? [];
                const startH = bi * 2;
                const endH   = startH + 2;
                return (
                  <span
                    key={bi}
                    style={{
                      flex: 1, height: 22, borderRadius: 3,
                      background: getColor(val, max),
                      cursor: val > 0 ? 'crosshair' : undefined,
                    }}
                    onMouseEnter={val > 0 ? (e) => show({
                      title: `${day} ${String(startH).padStart(2, '0')}:00–${String(endH).padStart(2, '0')}:00`,
                      subtitle: `${val} ${val === 1 ? 'query' : 'queries'}`,
                      rowsHeader: 'User ID',
                      rows: cellUsers.map(u => ({
                        label: u.id,
                        value: u.count,
                        color: '#8E42EE',
                      })),
                    }, e) : undefined}
                    onMouseMove={val > 0 ? move : undefined}
                    onMouseLeave={val > 0 ? hide : undefined}
                  />
                );
              })}
            </div>
          </div>
        ))}

        {/* Hour axis labels */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 2 }}>
          <span style={{ flex: '0 0 26px' }} />
          <div style={{ flex: 1, display: 'flex', gap: 'var(--an-sp-1)' }}>
            {HOUR_LABELS.map((h, i) => (
              <span
                key={i}
                style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--an-text-faint)', textAlign: 'center' }}
              >
                {h}
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Heatmap;
